// サロン見学情報ページ（index.tsx）と担当者編集モーダル（ContactModal.tsx）で
// 共有する型とヘルパー。
//
// 保存先はいずれも recruits/{companyId}_2027 の visitInfo マップ：
//   visitInfo.contact      = { name, role, message, photoUpdatedAt }
//   visitInfo.flow         = [ { label, durationMin, description }, ... ]
//   visitInfo.availability  = { weekly: {0..6: Band[]}, exceptions: {'YYYY-MM-DD': {bands: Band[]}} }
// 既存の visitSettings / visitExam* と同じドキュメントで、ドット表記更新のため
// 求人編集画面の保存とは衝突しない。

export type ContactData = {
  name: string;
  role: string;
  message: string;
  photoUpdatedAt: number; // 0 = 写真未設定。>0 は Storage 更新時刻(ms)でキャッシュバスト用
};

export type FlowStep = {
  label: string;
  durationMin: string; // 入力中は文字列で保持し、保存時に数値化する
  description: string;
};

export const emptyContact: ContactData = { name: '', role: '', message: '', photoUpdatedAt: 0 };
export const emptyStep = (): FlowStep => ({ label: '', durationMin: '', description: '' });

export const MAX_STEPS = 12;

export const photoUrl = (companyId: string, updatedAt: number) =>
  `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/visitContact%2F${companyId}?alt=media&t=${updatedAt}`;

/* -------------------------------------------------------------------------
   ③ 見学の受付枠（方式A：曜日×時間帯ルール ＋ 例外日）
   ------------------------------------------------------------------------- */

// 時間帯。午前 / 午後 / 夜 の3区分だけを扱う（実際の時刻はチャットで確定する前提）。
// 学生とサロンで認識をそろえるため、各区分の時間帯は固定で定義する：
//   午前 = 開店〜12:00 / 午後 = 12:00〜17:00 / 夜 = 17:00〜閉店
export type Band = 'am' | 'pm' | 'eve';

export const BANDS: Band[] = ['am', 'pm', 'eve'];
export const BAND_LABEL: Record<Band, string> = { am: '午前', pm: '午後', eve: '夜' };
export const BAND_SHORT: Record<Band, string> = { am: '前', pm: '後', eve: '夜' };

// 各区分の時間帯の定義（表示専用）。
export const BAND_TIME: Record<Band, string> = {
  am: '開店〜12:00',
  pm: '12:00〜17:00',
  eve: '17:00〜閉店',
};

// 0=日曜 .. 6=土曜
export const WEEKDAY_LABEL = ['日', '月', '火', '水', '木', '金', '土'];

export type Availability = {
  // 曜日ごとの受付時間帯。毎週この曜日は見学OK、というベースルール。
  weekly: Record<number, Band[]>;
  // 曜日ルールを上書きする日。bands:[] はその日を「受付なし」にする休止日、
  // bands:[...] はルールと違う枠を出す臨時日。キーはローカル日付 'YYYY-MM-DD'。
  exceptions: Record<string, { bands: Band[] }>;
};

export const emptyAvailability = (): Availability => ({
  weekly: { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] },
  exceptions: {},
});

// 未知の値が入っていても落ちないように整える。
const cleanBands = (v: any): Band[] =>
  Array.isArray(v) ? BANDS.filter((b) => v.includes(b)) : [];

export const normalizeAvailability = (raw: any): Availability => {
  const weekly: Record<number, Band[]> = {};
  for (let d = 0; d < 7; d++) weekly[d] = cleanBands(raw?.weekly?.[d]);

  const exceptions: Record<string, { bands: Band[] }> = {};
  const rawEx = raw?.exceptions;
  if (rawEx && typeof rawEx === 'object') {
    for (const key of Object.keys(rawEx)) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
        exceptions[key] = { bands: cleanBands(rawEx[key]?.bands) };
      }
    }
  }
  return { weekly, exceptions };
};

// ローカルタイムの 'YYYY-MM-DD'
export const ymd = (date: Date): string => {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// その日の受付時間帯を解決する。例外日があればそれを、なければ曜日ルールを使う。
export const resolveBands = (
  availability: Availability,
  date: Date,
): { bands: Band[]; isException: boolean } => {
  const key = ymd(date);
  if (Object.prototype.hasOwnProperty.call(availability.exceptions, key)) {
    return { bands: availability.exceptions[key].bands, isException: true };
  }
  return { bands: availability.weekly[date.getDay()] ?? [], isException: false };
};

export const sameBands = (a: Band[], b: Band[]): boolean =>
  a.length === b.length && BANDS.every((x) => a.includes(x) === b.includes(x));
